import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import store from "../../../../../../store";
import {setMultiLoader} from "../../../../../../actions/actionFunctions";
import SqftJsonApi from "../../../../api/sqftJsonApi";
import {CountertopPositions, WallPositions} from "../../../../helpers/sqftCabinetsDataMapper";
import ModelLoader from "./ModelLoader";
import {HDR_MAP_LOADER, loadHDRMap} from "./HDRMapLoader";

/**
 * Chat gpt v4
 */

class SeparateModelGLBViewer {
  spinner = null;
  animationFrameId = null;
  lightIntensity = 20;
  modelScale = 39.37;
  envMap = null;
  gltf = null;
  isMobile = false;
  modelLoader = null;
  modelsCenter = null;
  modelsSize = null;

  constructor(canvas, glbUrl, lightIntensity, isMobile) {
    this.canvas = canvas;
    this.glbUrl = glbUrl;
    this.lightIntensity = lightIntensity;
    this.isMobile = isMobile;
    //this.mouseDown = false;
    // this.initSpinner();
    this.initScene();
    this.modelLoader = new ModelLoader(this.scene, this.modelScale);

    Promise.all([this.loadCountertops(), this.loadCabinetSet(), this.loadWalls()]).then(() => {
      //console.log("Both cabinets and countertops are loaded.");
    });
  }

  initScene() {
    this.scene = new THREE.Scene();
    //this.scene.background = new THREE.Color(0xf0f0f0);
    //this.scene.background = new THREE.Color(0xbcc4ce);
    this.scene.background = new THREE.Color(0xcccccc);

    this.camera = new THREE.PerspectiveCamera(45, this.canvas.clientWidth / this.canvas.clientHeight, 0.1, 1000);
    this.camera.position.set(0, 0, 5);

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, preserveDrawingBuffer: true });
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    //this.renderer.setPixelRatio(this.isMobile ? window.devicePixelRatio * 0.5 : window.devicePixelRatio);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.useLegacyLights = false;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.gammaOutput = true;
    this.renderer.gammaFactor = 2.2;

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 0.1;
    this.controls.maxDistance = 100;

    loadHDRMap(this.scene, this.renderer, HDR_MAP_LOADER.pmremGeneratorEXR);

    //const hdrBackgroundPath = "/images/sqft-icons/3d/comfy_cafe_1k.hdr"
    //this.loadHDRAsBackground(hdrBackgroundPath);
    //this.addLightV3();
    //this.addLightV4();
    //this.addLightV2();
    //this.addLightGrey();
    this.animate();
  }

  normalizeModelSize(model, targetLength) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const currentLength = size.x;

    const scale = targetLength / currentLength;
    //console.log("scale", scale);
    model.scale.set(scale, scale, scale);
  }

  normalizeModelSizeFromConstantScale(model, ) {
    model.scale.set(this.modelScale, this.modelScale, this.modelScale);
  }

  loadCabinetSet() {
    const loadPromises = this.glbUrl.positions.map((position) => {
      return this.modelLoader.loadModel(position);
    });

    return Promise.allSettled(loadPromises).then((results) => {
      const models = results.filter(result => result.status === 'fulfilled' && result.value !== null).map(result => result.value);
      const totalBox = this.calculateModelsBox(models);
      this.modelsCenter = totalBox.getCenter(new THREE.Vector3());
      this.modelsSize = totalBox.getSize(new THREE.Vector3()).length();

      this.calculateAndSetCameraPosition(this.modelsCenter, this.modelsSize);
      if (this.scene) {
        this.addLightV10(this.modelsCenter);
      }

      store.dispatch(setMultiLoader({ activeStep: 8 })); // Disable multi-loader after all models are loaded
      setTimeout(() => {
        store.dispatch(setMultiLoader({ visible: false }));
      }, 500);
    }).catch(error => {
      console.log('Failed to load all models:', error);
    });
  }

  loadCountertops = async () => {
    const countertopPositions = CountertopPositions.prepareCountertopPositions(); // Получаем позиции для столешниц
    const loader = new GLTFLoader();

    const countertopPromises = countertopPositions.map(async (countertopPosition) => {
      try {
        const countertop = await SqftJsonApi.getCountertops(countertopPosition, {});
        //console.log("countertopPosition", countertopPosition);
        //console.log("countertop", countertop);
        if (countertop.s3Url) {
          loader.load(countertop.s3Url, (gltf) => { // Загружаем модель столешницы

            //v1 adjust model size to position in workspace
            gltf.scene.position.set(
              0,//countertopPosition.x / this.modelScale,
              countertopPosition.y / this.modelScale,
              0//countertopPosition.z / this.modelScale
            );
            //console.log(`countertop position: X=${countertopPosition.x}, Y=${countertopPosition.y}, Z=${countertopPosition.z}`)
            //console.log("gltf.scene.position", gltf.scene.position);

            //v2
            //this.normalizeModelSizeFromConstantScale(gltf.scene);
            //gltf.scene.rotation.y = countertopPosition.rotation;
            if (this.scene) {
              this.scene.add(gltf.scene);
            }
          }, undefined, (error) => {
            console.error(`Error loading countertop model from URL ${countertop.s3Url}:`, error);
          });
        } else {
          console.log(`No URL for countertop at position: ${countertopPosition}`);
        }
      } catch (error) {
        console.error(`Failed to get countertop data for position ${countertopPosition}`, error);
      }
    });

    return await Promise.all(countertopPromises);
  }

  loadWalls = async () => {
    const wallPositions = WallPositions.prepareWallPositions();
    const loader = new GLTFLoader();

    const wallPromises = wallPositions.map(async (wallPosition) => {
      try {
        const wall = await SqftJsonApi.getWall(wallPosition, {});
        //console.log("wallPosition", wallPosition);
        //console.log("wall", wall);
        if (wall.s3Url) {
          loader.load(wall.s3Url, (gltf) => { // Загружаем модель столешницы

            //v1 adjust model size to position in workspace
            gltf.scene.position.set(
              0,//wallPosition.x / this.modelScale,
              1 / this.modelScale ,//wallPosition.y / this.modelScale,
              0//wallPosition.z / this.modelScale
            );
            //console.log(`wall position: X=${wallPosition.x}, Y=${wallPosition.y}, Z=${wallPosition.z}`)
            //console.log("gltf.scene.position", gltf.scene.position);

            //v2
            //this.normalizeModelSizeFromConstantScale(gltf.scene);
            //gltf.scene.rotation.y = wallPosition.rotation;
            if (this.scene) {
              this.scene.add(gltf.scene);
            }
          }, undefined, (error) => {
            console.error(`Error loading wall model from URL ${wall.s3Url}:`, error);
          });
        } else {
          console.log(`No URL for wall at position: ${wallPosition}`);
        }
      } catch (error) {
        console.error(`Failed to get wall data for position ${wallPosition}`, error);
      }
    });

    return await Promise.all(wallPromises);
  }

  calculateModelsBox(models) {
    const totalBox = new THREE.Box3();
    models.forEach(model => {
      if (model instanceof THREE.Object3D) {
        totalBox.expandByObject(model);
      }
    });

    return totalBox
  }

  calculateAndSetCameraPosition(center, size) {
    this.controls.reset();
    this.controls.target.copy(center);
    this.controls.maxDistance = size * 10;

    this.camera.position.copy(center.clone().add(new THREE.Vector3(size, size / 5, size * 2)));
    this.camera.near = size / 100;
    this.camera.far = size * 100;
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();

    // if (this.controls) {
    //   this.controls.reset();
    //   this.controls.maxDistance = size * 10;
    // }
    //
    // if (this.camera) {
    //   this.camera.position.copy(center);
    //   this.camera.position.x += size; // far distance
    //   this.camera.position.y += size / 5.0;
    //   //this.camera.position.z += size;
    //   this.camera.position.z *= -1; // rotate distance
    //
    //   this.camera.near = size / 100;
    //   this.camera.far = size * 100;
    //   this.camera.updateProjectionMatrix();
    // }
  }

  correctGltfProps(gltf) {
    gltf.scene.children.forEach(element => {
      // console.log(element);
      if (element.isMesh) {
        element.children.forEach(child => {
          child.receiveShadow = true;
        });
        element.castShadow = true;
        element.receiveShadow = true;
        element.visible = false;
      }
      if (element.isGroup) {
        element.children.forEach(e => {
          void e;
          if (element.isMesh) {
            element.castShadow = true;
            element.receiveShadow = true;
          }
          if (element.isLight) {
            element.visible = false;
          }
        })
      }
      if (element.isLight) {
        element.intensity *= 0.05;
        console.log(element);
        element.visible = false;
      }
    });

    return gltf
  }

  addLight(boxCenter) {
    //console.log("boxCenter", boxCenter);
    //const maxSize = Math.max(boxSize.x, boxSize.y, boxSize.z);
    const lightOffset = 4.5;
    const baseHeight = 2.5;

    const dynamicIntensity = this.lightIntensity ;

    //console.log(`maxSize: ${maxSize}, lightOffset: ${lightOffset}, baseHeight: ${baseHeight}`);
    console.log(`this.lightIntensity: ${this.lightIntensity }`);
    console.log(`dynamicIntensity: ${dynamicIntensity}`);

    const lightPositions = [
      // Central light
      { x: 0, y: baseHeight / 2, z: 5 },
      { x: 0, y: baseHeight * 2, z: 5 },

      // Upper corner Light
      { x: lightOffset, y: baseHeight, z: lightOffset },
      { x: -lightOffset, y: baseHeight, z: lightOffset },
      { x: lightOffset, y: baseHeight, z: -lightOffset },
      { x: -lightOffset, y: baseHeight, z: -lightOffset },

      // Bottom light
      { x: lightOffset, y: -baseHeight, z: lightOffset },
      { x: -lightOffset, y: -baseHeight, z: lightOffset },
      { x: lightOffset, y: -baseHeight, z: -lightOffset },
      { x: -lightOffset, y: -baseHeight, z: -lightOffset }
    ];


    lightPositions.forEach(pos => {
      let light = new THREE.PointLight(0xffffff, dynamicIntensity, 0, 2);
      light.position.set(boxCenter.x + pos.x, boxCenter.y + pos.y, boxCenter.z + pos.z);
      light.castShadow = true;
      this.scene.add(light);
      //console.log(`Light position: x: ${pos.x}, y: ${pos.y}, z: ${pos.z}, Intensity: ${dynamicIntensity}`);
    });

    // Добавление окружающего света для общего освещения
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(boxCenter.x, boxCenter.y, boxCenter.z + 10);
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);
  }

  addLightV9(boxCenter) {
    this.renderer.toneMappingExposure = 0.1;
    this.lightIntensity = 800;
    this.addLight(boxCenter);
  }
  addLightV10(boxCenter) {
    this.renderer.toneMappingExposure = 1;
    this.lightIntensity = 20;

    this.addLight(boxCenter);
  }

  updateSceneLighting(type) {
    // Delete current lights
    this.scene.children = this.scene.children.filter(child => !(child instanceof THREE.Light));

    // Create and add new light
    switch (type) {
      case 'addLightV9':
        this.addLightV9(this.modelsCenter);
        break;
      case 'addLightV10':
        this.addLightV10(this.modelsCenter);
        break;
      default:
        console.warn(`Unknown lighting type: ${type}`);
        break;
    }
  }

  updateLightIntensity(intensity) {
    this.lightIntensity = intensity
    this.scene.children.forEach(child => {
      if (child instanceof THREE.PointLight) {
        //console.log("child.intensity", child.intensity);
        child.intensity = intensity;
      }
    });
  }

  animate() {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  dispose() {
    // Stop the animation loop
    this.stop();

    // Dispose of all resources
    //this.scene.dispose(); //todo
    this.renderer.dispose();
    this.controls.dispose();

    // Remove spinner
    // if (this.spinner) {
    //   ReactDOM.unmountComponentAtNode(this.spinner);
    //   this.spinner.remove();
    //   this.spinner = null;
    // }

    // Nullify properties to free up memory
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.canvas = null;
  }

  onMouseDown() {
  }

  onMouseMove() {
  }

  onMouseUp() {
  }

  onWheel() {
  }

  onWindowResize() {
    this.camera.aspect = this.canvas.clientWidth / this.canvas.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
  }

  // This methods will rotate scene in case OrbitControls wont fit everything

  // onMouseDown(event) {
  //   this.mouseDown = true;
  //   this.lastPosition = {
  //     x: event.clientX,
  //     y: event.clientY
  //   };
  // }
  //
  // onMouseMove(event) {
  //   if (!this.mouseDown) return;
  //
  //   const deltaX = event.clientX - this.lastPosition.x;
  //   const deltaY = event.clientY - this.lastPosition.y;
  //
  //   this.camera.rotation.y += deltaX * 0.01;
  //   this.camera.rotation.x += deltaY * 0.01;
  //
  //   this.lastPosition = {
  //     x: event.clientX,
  //     y: event.clientY
  //   };
  //
  //   this.renderer.render(this.scene, this.camera);
  // }
  //
  // onMouseUp() {
  //   this.mouseDown = false;
  // }
  //
  // onWheel(event) {
  //   const delta = event.deltaY * 0.001;
  //   this.camera.position.z += delta;
  //
  //   this.renderer.render(this.scene, this.camera);
  // }
  // onWindowResize() {
  //
  // }
}

export default SeparateModelGLBViewer;
